import { FormattedMessage, FormattedNumber } from 'react-intl';
import BarChart from '@/components/BarChart/BarChart';
import { getSellerReviewsUrl } from '@/utils/urls';
import isEqual from 'react-fast-compare';
import React from 'react';
import styles from './RatingsCounter.module.scss';

type Props = {
    fullPage: boolean;
    ratings: any;
    seller: any;
    updateFilter: Function;
};

const labelCreator = (percent: number, count: number, totalReviews: number) => {
    const muted = Math.round(percent) + '%';
    const labelStyle = totalReviews > 0 ? styles.blue : '';
    return (
        <span className={styles.rightLabel}>
            <span>{muted.length === 2 ? muted.padStart(4) : muted}</span>
            <span className={labelStyle}>
                {' '}
                (
                <span className={styles.reviewCount}>
                    <FormattedNumber value={count || 0} />
                </span>
                )
            </span>
        </span>
    );
};

export const calculatePercentage = (count: number, totalReviews: number) => {
    return totalReviews > 0 ? (count / totalReviews) * 100 : 0;
};

const RatingsCounter = (props: Props) => {
    const { fullPage, ratings, seller, updateFilter } = props;
    const {
        fiveStarReviews,
        fourStarReviews,
        oneStarReviews,
        threeStarReviews,
        totalReviews,
        twoStarReviews,
    } = ratings;
    const { name, sellerId } = seller;
    const starRatings = [
        {
            label: 'stars.five',
            name: '5star',
            percent: calculatePercentage(fiveStarReviews, totalReviews),
            reviews: fiveStarReviews,
        },
        {
            label: 'stars.four',
            name: '4star',
            percent: calculatePercentage(fourStarReviews, totalReviews),
            reviews: fourStarReviews,
        },
        {
            label: 'stars.three',
            name: '3star',
            percent: calculatePercentage(threeStarReviews, totalReviews),
            reviews: threeStarReviews,
        },
        {
            label: 'stars.two',
            name: '2star',
            percent: calculatePercentage(twoStarReviews, totalReviews),
            reviews: twoStarReviews,
        },
        {
            label: 'stars.one',
            name: '1star',
            percent: calculatePercentage(oneStarReviews, totalReviews),
            reviews: oneStarReviews,
        },
    ];

    const updateStarFilter = (filter: string) => updateFilter(filter);
    const rows = starRatings.map((rating) => {
        return {
            filter: () => updateStarFilter(rating.name),
            filterLink: getSellerReviewsUrl(sellerId, name, rating.name),
            leftLabel: (
                <span className={styles.label}>
                    <FormattedMessage id={rating.label} />
                </span>
            ),
            percent: rating.percent,
            rightLabel: labelCreator(rating.percent, rating.reviews, totalReviews),
        };
    });

    return <BarChart fullPage={fullPage} rightLabelStyles={styles.rightLabel} rows={rows} />;
};

export default React.memo<Props>(RatingsCounter, isEqual);
